'use client';

import AnchorButton from '@components/shared/AnchorButton/AnchorButton';
import HeaderButtons from '@components/shared/Header/components/HeaderButtons';
import useWindowProperty from '@lib/hooks/useWindowProperty';
import { HeroOverlapType } from '@lib/sanity/queries/modules/heroOverlap/types';
import { useStatsigExperiment } from '@lib/Statsig';
import { WEBSITE_HOMEPAGE_HERO_TEXTBOX_OR_BUTTONS } from '@lib/Statsig/constants';
import { useClientConfig } from '@providers/ClientConfigProvider';
import { useCallback, useState } from 'react';
import HeroTextBox from './HeroTextBox';

export default function HeroTextBoxExperiment({
  heroTextBox,
}: {
  heroTextBox?: NonNullable<NonNullable<HeroOverlapType['hero']>['heroTextBox']> | null;
}) {
  const config = useClientConfig();
  const getExperimentValue = useStatsigExperiment();
  const [isShowTextBox, setIsShowTextBox] = useState<boolean | null>(null);

  const fetchStatsigExperiment = useCallback(async () => {
    const value = await getExperimentValue(WEBSITE_HOMEPAGE_HERO_TEXTBOX_OR_BUTTONS, 'show_textbox', true);
    setIsShowTextBox(!!value);
  }, [getExperimentValue]);

  // Watch for window.anonymousId to be assigned (handles immediate check, polling, and 10s timeout fallback)
  useWindowProperty<string>('anonymousId', fetchStatsigExperiment);

  if (!heroTextBox || isShowTextBox === null) {
    return (
      <div className="col-span-full h-[58.8px] flex-col items-center justify-center gap-4 self-center sm:h-[74px] sm:flex-row lg:h-[114px]" />
    );
  }

  if (isShowTextBox) {
    return (
      <>
        <HeroTextBox
          className="col-span-4 hidden sm:col-span-6 sm:block 
                        md:col-span-6 md:col-start-2 
                        lg:col-span-8 lg:col-start-3 "
          data={heroTextBox}
        />
        <AnchorButton
          button={
            heroTextBox.searchButton
              ? {
                  ...heroTextBox.searchButton,
                  size: 'xl',
                  variant: 'primary',
                  url: heroTextBox.urlWhenEmpty || '',
                }
              : undefined
          }
          buttonClassName="col-span-full block sm:hidden"
        />
      </>
    );
  }

  return (
    <HeaderButtons
      className="col-span-full flex h-auto flex-col items-center justify-center gap-4 self-center sm:h-[74px] sm:flex-row lg:h-[114px]"
      buttonPrimary={{
        text: { value: 'Get started' },
        url: `${config.APP_URL}?signup=true&designTypeId=2&action=go-to-templates&internal_source=homepage&internal_medium=HeroGetStarted&internal_campaign=VersionB`,
        eventTracking: {
          elementName: 'cta-button',
          dataSource: 'homepage',
          dataMedium: 'HeroGetStarted',
          dataExperiment: WEBSITE_HOMEPAGE_HERO_TEXTBOX_OR_BUTTONS,
          dataVariant: 'VersionB',
        },
      }}
      size="xl"
    />
  );
}
